import { apiRequest } from '../lib/httpClient'
import type {
  StaffGridRow,
  StaffGridApiRow,
  StaffDetailsFormValues,
  StaffDataRow,
  StaffDataApiRow,
  StaffMonthlyBudgetRow,
  StaffMonthlyBudgetApiRow,
  StaffGridFilters,
  StaffUpdatePayload,
  StaffAddPayload,
  StaffDataUpdatePayload,
  StaffMonthlyBudgetUpdatePayload,
  StaffListApiResponse,
  StaffUploadPayload,
  StaffUploadResponse,
  UserOption,
} from '../types/staff'
export { fileToBase64 } from '../lib/files'

// --- Normalizers ---

function mapStaffGridRow(row: StaffGridApiRow): StaffGridRow {
  return {
    uuid: row.uuid || row.Uuid || '',
    staffName: row.staffName || row.StaffName || '',
    teamName: row.teamName || row.TeamName || '',
    teamManager: row.teamManager || row.TeamManager || '',
    employmentStartDate: row.employmentStartDate || row.EmploymentStartDate || null,
    employmentEndDate: row.employmentEndDate || row.EmploymentEndDate || null,
  }
}

function mapStaffDataRow(row: StaffDataApiRow): StaffDataRow {
  return {
    id: row.id || row.Id || '',
    uuid: row.uuid || row.Uuid || '',
    effectiveDateStart: row.effectiveDateStart || row.EffectiveDateStart || null,
    effectiveDateEnd: row.effectiveDateEnd || row.EffectiveDateEnd || null,
    employmentStatus: row.employmentStatus || row.EmploymentStatus || '',
    monHours: row.monHours ?? row.MonHours ?? '',
    tueHours: row.tueHours ?? row.TueHours ?? '',
    wedHours: row.wedHours ?? row.WedHours ?? '',
    thuHours: row.thuHours ?? row.ThuHours ?? '',
    friHours: row.friHours ?? row.FriHours ?? '',
    productivity: row.productivity ?? row.Productivity ?? '',
    annualSalary: row.annualSalary ?? row.AnnualSalary ?? '',
    salaryHourlyRate: row.salaryHourlyRate ?? row.SalaryHourlyRate ?? '',
    recoverability: row.recoverability ?? row.Recoverability ?? '',
    billing: row.billing ?? row.Billing ?? '',
  }
}

function mapStaffMonthlyBudgetRow(row: StaffMonthlyBudgetApiRow): StaffMonthlyBudgetRow {
  return {
    staffMonthlyBudgetId: row.staffMonthlyBudgetId || row.StaffMonthlyBudgetId || '',
    staffId: row.staffId || row.StaffId || '',
    clientId: row.clientId || row.ClientId || '',
    breakdown: row.breakdown || row.Breakdown || '',
    month: row.month || row.Month || null,
    budget: row.budget ?? row.Budget ?? '',
  }
}

function mapUserOption(row: Record<string, unknown>): UserOption {
  return {
    name: (row['name'] || row['Name'] || '') as string,
    value: (row['value'] || row['Value'] || '') as string,
  }
}

// --- Grid list ---

export async function fetchClientUserOptions(clientId: string): Promise<UserOption[]> {
  const payload = await apiRequest<Record<string, unknown>[] | Record<string, unknown>>('client/ui/user/options', {
    method: 'GET',
    query: { clientId },
  })

  const root = (payload || {}) as Record<string, unknown>
  const options = ((root['options'] || root['Options'] || {}) as Record<string, unknown>) || {}
  const rawUsers = Array.isArray(payload)
    ? payload
    : (options['users'] || options['Users'] || options['userOptions'] || root['users'] || root['Users']) as
        | Record<string, unknown>[]
        | undefined

  return (rawUsers || []).map(mapUserOption)
}

export async function fetchAllStaffDetails(
  clientId: string,
  filters: StaffGridFilters
): Promise<{ rows: StaffGridRow[]; users: UserOption[] }> {
  const payload = await apiRequest<StaffListApiResponse | StaffGridApiRow[]>(
    'client/ui/staff/details',
    {
      method: 'GET',
      query: { clientId, staffName: filters.staffName || undefined },
    }
  )

  if (Array.isArray(payload)) {
    return { rows: payload.map(mapStaffGridRow), users: [] }
  }

  const rawRows = payload.rows || payload.Rows || []
  const rawUsers = (payload.users || payload.Users || []) as Record<string, unknown>[]
  return {
    rows: rawRows.map(mapStaffGridRow),
    users: rawUsers.map(mapUserOption),
  }
}

// --- Single record ---

export async function fetchStaffDetailsById(
  uuid: string,
  clientId: string
): Promise<{ details: StaffDetailsFormValues; users: UserOption[] }> {
  const payload = await apiRequest<Record<string, unknown>>('client/ui/staff/detailsById', {
    method: 'GET',
    query: { uuid, clientId },
  })

  const raw = (payload['staffDetails'] || payload['StaffDetails'] || payload) as Record<string, unknown>
  const teamManagerRaw = (raw['teamManager'] || raw['TeamManager'] || '') as string
  const startDate = (raw['employmentStartDate'] || raw['EmploymentStartDate'] || null) as string | null
  const endDate = (raw['employmentEndDate'] || raw['EmploymentEndDate'] || null) as string | null

  const details: StaffDetailsFormValues = {
    staffName: (raw['staffName'] || raw['StaffName'] || '') as string,
    email: (raw['email'] || raw['Email'] || '') as string,
    teamName: (raw['teamName'] || raw['TeamName'] || '') as string,
    teamManager: teamManagerRaw ? teamManagerRaw.split('|').filter(Boolean) : [],
    additionalAccess: (raw['additionalAccess'] || raw['AdditionalAccess'] || '') as string,
    employmentStartDate: startDate ? startDate.split('T')[0] || startDate : null,
    employmentEndDate: endDate ? endDate.split('T')[0] || endDate : null,
  }

  const rawUsers = (payload['users'] || payload['Users'] || []) as Record<string, unknown>[]
  return { details, users: rawUsers.map(mapUserOption) }
}

export async function fetchStaffDataById(
  uuid: string,
  clientId: string
): Promise<StaffDataRow[]> {
  const data = await apiRequest<StaffDataApiRow[]>('client/ui/staff/data', {
    method: 'GET',
    query: { uuid, clientId },
  })

  const rows = (Array.isArray(data) ? data : []).map(mapStaffDataRow)
  return rows.sort((a, b) => {
    const dateA = a.effectiveDateEnd || ''
    const dateB = b.effectiveDateEnd || ''
    return dateB.localeCompare(dateA)
  })
}

export async function fetchStaffMonthlyBudgetById(
  uuid: string,
  clientId: string
): Promise<StaffMonthlyBudgetRow[]> {
  const data = await apiRequest<StaffMonthlyBudgetApiRow[]>('client/ui/staff/monthlyBudgetById', {
    method: 'GET',
    query: { uuid, clientId },
  })

  const rows = (Array.isArray(data) ? data : []).map(mapStaffMonthlyBudgetRow)
  return rows.sort((a, b) => {
    const monthA = a.month || ''
    const monthB = b.month || ''
    return monthB.localeCompare(monthA)
  })
}

// --- Mutations ---

export async function updateStaffDetailsById(payload: StaffUpdatePayload): Promise<void> {
  await apiRequest<void>('client/ui/staff/updateDetailsById', {
    method: 'POST',
    body: payload,
  })
}

export async function updateStaffData(payload: StaffDataUpdatePayload): Promise<void> {
  await apiRequest<void>('client/ui/staff/updateData', {
    method: 'POST',
    body: payload,
  })
}

export async function updateStaffMonthlyBudget(
  payload: StaffMonthlyBudgetUpdatePayload
): Promise<void> {
  await apiRequest<void>('client/ui/staff/updateMonthlyBudget', {
    method: 'POST',
    body: payload,
  })
}

export async function addStaffDetails(payload: StaffAddPayload): Promise<void> {
  await apiRequest<void>('client/ui/staff/add', {
    method: 'POST',
    body: payload,
  })
}

export async function deleteStaffById(uuid: string, clientId: string): Promise<void> {
  await apiRequest<void>('client/ui/staff/delete', {
    method: 'DELETE',
    query: { uuid, clientId },
  })
}

// --- Downloads ---
// The API returns base64-encoded CSV strings (not binary blobs).

function base64ToBlob(base64: string, contentType = 'text/csv'): Blob {
  const src = `data:${contentType};base64,${base64}`
  const link = document.createElement('a')
  link.href = src
  // Convert data-URL to a proper Blob so callers can use triggerDownload as-is
  const byteChars = atob(base64)
  const byteArray = new Uint8Array(byteChars.length)
  for (let i = 0; i < byteChars.length; i++) {
    byteArray[i] = byteChars.charCodeAt(i)
  }
  return new Blob([byteArray], { type: contentType })
}

export async function downloadStaffDetailsById(uuid: string, clientId: string): Promise<Blob> {
  const base64 = await apiRequest<string>('client/ui/staff/downloadDetails', {
    method: 'GET',
    query: { uuid, clientId },
  })
  return base64ToBlob(base64)
}

export async function downloadStaffDataById(uuid: string, clientId: string): Promise<Blob> {
  const base64 = await apiRequest<string>('client/ui/staff/downloadData', {
    method: 'GET',
    query: { uuid, clientId },
  })
  return base64ToBlob(base64)
}

export async function downloadStaffMonthlyBudget(uuid: string, clientId: string): Promise<Blob> {
  const base64 = await apiRequest<string>('client/ui/staff/downloadMonthlyBudget', {
    method: 'GET',
    query: { uuid, clientId },
  })
  return base64ToBlob(base64)
}

// --- Uploads ---

export async function uploadStaffDetails(payload: StaffUploadPayload): Promise<StaffUploadResponse> {
  return apiRequest<StaffUploadResponse>('client/ui/upload/staff', {
    method: 'POST',
    body: payload,
  })
}

export async function uploadStaffData(payload: StaffUploadPayload): Promise<StaffUploadResponse> {
  return apiRequest<StaffUploadResponse>('client/ui/upload/staffData', {
    method: 'POST',
    body: payload,
  })
}

export async function uploadStaffMonthlyBudget(payload: StaffUploadPayload): Promise<boolean> {
  return apiRequest<boolean>('client/ui/upload/staffMonthlyBudget', {
    method: 'POST',
    body: payload,
  })
}
